type DeferredEndDragScheduler = {
  cancel: () => void;
  flush: () => void;
  schedule: (callback: () => void) => void;
};

type CreateDeferredEndDragHandlersArgs = {
  handleTopDragEndCapture: (event: Event) => void;
  handleTopDragStart: (event: Event) => void;
  handleTopDrop: (event: Event) => void;
  isDragging: () => boolean;
  isFixEnabled: () => boolean;
  scheduler: DeferredEndDragScheduler;
  teardown: () => void;
};

export type DeferredEndDragHandlers = {
  handleTopDragEndCapture: (event: Event) => void;
  handleTopDragStart: (event: Event) => void;
  handleTopDrop: (event: Event) => void;
  teardown: () => void;
};

/**
 * react-dnd's HTML5 backend ends the drag on `dragend` and then throws if a later
 * `drop` calls hover. Some enterprise browsers/extensions deliver drop after dragend.
 * When the fix is on, defer ending until drop (or the scheduler fires) so the drop
 * can still complete. When a new drag starts, flush the pending end first so
 * dnd-core is not still dragging.
 */
export const createDeferredEndDragHandlers = ({
  handleTopDragEndCapture,
  handleTopDragStart,
  handleTopDrop,
  isDragging,
  isFixEnabled,
  scheduler,
  teardown,
}: CreateDeferredEndDragHandlersArgs): DeferredEndDragHandlers => {
  return {
    handleTopDragEndCapture: (event) => {
      if (!isFixEnabled()) {
        handleTopDragEndCapture(event);
        return;
      }

      scheduler.schedule(() => {
        handleTopDragEndCapture(event);
      });
    },
    handleTopDragStart: (event) => {
      scheduler.flush();
      handleTopDragStart(event);
    },
    handleTopDrop: (event) => {
      if (!isFixEnabled()) {
        handleTopDrop(event);
        return;
      }

      scheduler.cancel();
      if (!isDragging()) {
        event.preventDefault();
        return;
      }
      handleTopDrop(event);
    },
    teardown: () => {
      scheduler.cancel();
      teardown();
    },
  };
};

export const createDeferredEndDragTimeoutScheduler = (delayMs: number): DeferredEndDragScheduler => {
  let pending: (() => void) | undefined;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;

  const clearTimer = () => {
    if (timeoutId === undefined) {
      return;
    }

    clearTimeout(timeoutId);
    timeoutId = undefined;
  };

  return {
    cancel: () => {
      clearTimer();
      pending = undefined;
    },
    flush: () => {
      clearTimer();
      const callback = pending;
      pending = undefined;
      callback?.();
    },
    schedule: (callback) => {
      clearTimer();
      pending = callback;
      timeoutId = setTimeout(() => {
        timeoutId = undefined;
        pending = undefined;
        callback();
      }, delayMs);
    },
  };
};
